import CloseIcon from "@mui/icons-material/Close";
import {
	Box,
	Button,
	Divider,
	FormControl,
	Grid,
	Icon,
	IconButton,
	InputLabel,
	MenuItem,
	Modal,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { BorderoAccountType, ICreateBordero, ISinisterValues, SinisterService } from "../../../../shared/services/api/sinister/SinisterService";
import { TableDetailBordero } from "../TableDetailBordero";
import { useEffect, useState } from "react";
import { addDays, format } from "date-fns";
import { useToast } from "../../../../shared/hooks";
import { ToastContent } from "../../../../shared/components";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../../shared/guards/ProfileGuard";
import { useProfile } from "../../../../context/ProfileContext";

// Dados bancários já vêm do sinistro (ISinisterList.client) — sem fetch extra, só repassados.
export interface IBorderoClientBankData {
	bank_code?: string;
	agency_number?: string;
	agency_digit?: string;
	account_number?: string;
	account_digit?: string;
	pix_key?: string;
	pix_key_type?: string;
}

interface ModalGenerateProps {
	open: boolean;
	onClose: () => void;
	onSuccess: () => void;
	rows: ISinisterValues[];
	clientId: string;
	sinisterId: string;
	client?: IBorderoClientBankData;
}

const MIN_DAYS_FROM_TODAY = 15;
const DEFAULT_DAYS_FROM_TODAY = 30;

export const ModalGenerateBordero = ({ open, onClose, onSuccess, rows, clientId, sinisterId, client }: ModalGenerateProps) => {
	const theme = useTheme();
	const smDown = useMediaQuery(theme.breakpoints.down("sm"));
	const [isLoading, setIsLoading] = useState(false);
	const toastHandler = useToast();
	const { isSuperAdmin } = useProfile();

	const minDueDate = format(addDays(new Date(), MIN_DAYS_FROM_TODAY), "yyyy-MM-dd");

	const [dueDate, setDueDate] = useState(minDueDate);
	const [bankCode, setBankCode] = useState("");
	const [agencyNumber, setAgencyNumber] = useState("");
	const [agencyDigit, setAgencyDigit] = useState("");
	const [accountNumber, setAccountNumber] = useState("");
	const [accountDigit, setAccountDigit] = useState("");
	const [accountType, setAccountType] = useState<BorderoAccountType>("corrente");
	const [pixKey, setPixKey] = useState("");
	const [pixKeyType, setPixKeyType] = useState("");
	const [indemnityWorksheet, setIndemnityWorksheet] = useState("");

	useEffect(() => {
		if (!open) return;
		setDueDate(format(addDays(new Date(), DEFAULT_DAYS_FROM_TODAY), "yyyy-MM-dd"));
		setBankCode(client?.bank_code ?? "");
		setAgencyNumber(client?.agency_number ?? "");
		setAgencyDigit(client?.agency_digit ?? "");
		setAccountNumber(client?.account_number ?? "");
		setAccountDigit(client?.account_digit ?? "");
		setAccountType("corrente");
		setPixKey(client?.pix_key ?? "");
		setPixKeyType(client?.pix_key_type ?? "");
		setIndemnityWorksheet("");
	}, [open, client]);

	const handleDueDateChange = (value: string) => {
		if (value && value < minDueDate) {
			setDueDate(minDueDate);
			return;
		}
		setDueDate(value);
	};

	const handleGenerateBordero = async () => {
		if (!(isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.SinisterValuesOptions, PermissionActionsEnum.GenerateBordero))) {
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Sem permissão" content="Seu usuário não tem permissão para gerar Borderô." />,
			});
			return;
		}

		setIsLoading(true);
		try {
			const ivalores = rows?.map((element) => ({
				id: element.id || "",
			}));

			const data: ICreateBordero = {
				sinisterValuesRequest: ivalores,
				dueDate: `${dueDate} 03:00:00`,
				sinisterId: sinisterId,
				clientId: clientId,
				bankCode: bankCode || undefined,
				agencyNumber: agencyNumber || undefined,
				agencyDigit: agencyDigit || undefined,
				accountNumber: accountNumber || undefined,
				accountDigit: accountDigit || undefined,
				accountType,
				pixKey: pixKey || undefined,
				pixKeyType: pixKeyType || undefined,
				indemnityWorksheet: indemnityWorksheet || undefined,
			};

			const result = await SinisterService.generateBordero(sinisterId, data);

			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title="Sucesso" content="Registro gravado com sucesso" />,
			});

			onSuccess();
			onClose();
		} catch (error: any) {
			console.error("Erro ao gerar bordereau:", error);
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: (
					<ToastContent title="Erro ao gerar bordereau" content={error?.message || "Ocorreu um erro inesperado. Tente novamente mais tarde."} />
				),
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<>
			<LoadingComp isopen={isLoading} />
			<Modal open={open} onClose={onClose}>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						bgcolor: "background.paper",
						boxShadow: 24,
						p: 4,
						width: "95%",
						maxWidth: 720,
						maxHeight: "90vh",
						overflowY: "auto",
						borderRadius: 3,
					}}
				>
					<Grid container justifyContent="space-between" alignItems="center">
						<Typography variant="h4" sx={{ color: "primary.main", fontWeight: 300 }}>
							Geração de Borderô
						</Typography>
						<IconButton onClick={onClose} size="small">
							<CloseIcon />
						</IconButton>
					</Grid>

					<Divider sx={{ my: 2 }} />

					{/* Usa as linhas diretamente e desativa o fetch */}
					<TableDetailBordero disableFetch rows={rows} />

					<Divider sx={{ my: 3 }} />

					<Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
						Vencimento
					</Typography>
					<Grid container spacing={2} alignItems="center">
						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								size="small"
								type="date"
								label="Data limite"
								value={dueDate}
								onChange={(e) => handleDueDateChange(e.target.value)}
								onBlur={(e) => handleDueDateChange(e.target.value)}
								inputProps={{ min: minDueDate }}
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Typography variant="caption" color="text.secondary">
								Data recomendada = {format(addDays(new Date(), DEFAULT_DAYS_FROM_TODAY), "dd/MM/yyyy")} (padrão de 30 dias). Não é
								possível marcar vencimento a menos de 15 dias da geração.
							</Typography>
						</Grid>
					</Grid>

					<Divider sx={{ my: 3 }} />

					<Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
						Dados bancários
					</Typography>
					<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
						Pré-preenchido com o cadastro da imobiliária — confirme ou ajuste conforme a declaração de débitos recebida.
					</Typography>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={4}>
							<TextField fullWidth size="small" label="Banco" value={bankCode} onChange={(e) => setBankCode(e.target.value)} />
						</Grid>
						<Grid item xs={6} sm={3}>
							<TextField fullWidth size="small" label="Agência" value={agencyNumber} onChange={(e) => setAgencyNumber(e.target.value)} />
						</Grid>
						<Grid item xs={6} sm={2}>
							<TextField fullWidth size="small" label="Dígito" value={agencyDigit} onChange={(e) => setAgencyDigit(e.target.value)} />
						</Grid>
						<Grid item xs={12} sm={3}>
							<FormControl fullWidth size="small">
								<InputLabel id="bordero-account-type-label">Tipo</InputLabel>
								<Select
									labelId="bordero-account-type-label"
									label="Tipo"
									value={accountType}
									onChange={(e: SelectChangeEvent) => setAccountType(e.target.value as BorderoAccountType)}
								>
									<MenuItem value="corrente">Conta Corrente</MenuItem>
									<MenuItem value="poupanca">Poupança</MenuItem>
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={6} sm={4}>
							<TextField fullWidth size="small" label="Número da conta" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} />
						</Grid>
						<Grid item xs={6} sm={2}>
							<TextField fullWidth size="small" label="Dígito" value={accountDigit} onChange={(e) => setAccountDigit(e.target.value)} />
						</Grid>
						<Grid item xs={12} sm={3}>
							<TextField fullWidth size="small" label="Tipo de chave Pix" value={pixKeyType} onChange={(e) => setPixKeyType(e.target.value)} />
						</Grid>
						<Grid item xs={12} sm={3}>
							<TextField fullWidth size="small" label="Chave Pix" value={pixKey} onChange={(e) => setPixKey(e.target.value)} />
						</Grid>
					</Grid>

					<Divider sx={{ my: 3 }} />

					<Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
						Planilha de indenização
					</Typography>
					<TextField
						fullWidth
						size="small"
						label="Número da indenização"
						placeholder="Ex: P1, P2, P3, PF..."
						value={indemnityWorksheet}
						onChange={(e) => setIndemnityWorksheet(e.target.value)}
						helperText="Independente do número sequencial automático do borderô."
					/>

					<Grid container justifyContent="flex-end" flexDirection={smDown ? "column" : "row"} gap={2} mt={3}>
						<Button color="inherit" variant="contained" startIcon={<Icon>arrow_back</Icon>} onClick={onClose} sx={{ minWidth: 140 }}>
							Voltar
						</Button>
						<Button
							color="primary"
							variant="contained"
							startIcon={<Icon>check</Icon>}
							onClick={() => {
								handleGenerateBordero();
							}}
							sx={{ minWidth: 140 }}
						>
							Confirmar
						</Button>
					</Grid>
				</Box>
			</Modal>
		</>
	);
};
